<svelte:options customElement="hb-input-array-objects" />

<script lang="ts">
  /**
   * Svelte Input Array of Objects WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type { FormSchemaEntry, Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  let { schemaentry = undefined, show_validation = "no" }: Component = $props();

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }
  import pkg from "../../../package.json";
  import { addComponent } from "wc-js-utils";
  import type {
    Component as TableComponent,
    Events as TableEvents,
  } from "../table/types/webcomponent.type";
  import type { Events as FormEvents } from "../form/types/webcomponent.type";
  import type { FormSchema } from "../form/types/webcomponent.type";
  import { onMount } from "svelte";

  let value: any[] = $state([]);

  /** Matches Bulma host theme hooks (`bulma-wc-host-theme-tokens.scss`). */
  function documentExplicitColorScheme(): "dark" | "light" | null {
    for (const el of [
      $host() as Element | undefined,
      document.documentElement,
      document.body,
    ]) {
      if (!el) continue;
      const dt = el.getAttribute("data-theme");
      if (dt === "dark") return "dark";
      if (dt === "light") return "light";
      if (el.classList.contains("theme-dark")) return "dark";
      if (el.classList.contains("theme-light")) return "light";
    }
    return null;
  }

  function computeColorSchemeDark(): boolean {
    const explicit = documentExplicitColorScheme();
    if (explicit === "dark") return true;
    if (explicit === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  /** Nested `hb-form` submit: outlined primary in dark / explicit dark theme. */
  let nested_form_buttons_outlined = $state<"yes" | "no">("no");

  let tableHeaders: TableComponent["headers"] = $state([]);
  let tableRows: TableComponent["rows"] = $state([]);

  const tableActions: TableComponent["actions"] = [
    {
      name: "delete",
      type: "icon",
      iconOrText: "x-octagon-fill",
    },
    // {
    // 	name: "edit",
    // 	type: "icon",
    // 	iconOrText: "pencil-square",
    // },
  ];
  let arrayOfResults: { schema: FormSchema; id: string }[] = $state([]);
  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let valid = $derived.by(() => {
    if (field) {
      if (field?.required) {
        if (value?.length && typeof value === "object") {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } else {
      return false;
    }
  });
  function recalc() {
    const f = field;
    if (!f) return;
    value =
      value && Array.isArray(value) && value.length
        ? value
        : f.value
          ? f.value
          : [];
    const templateSchema = Array.isArray(f.params?.schema)
      ? f.params.schema
      : [];
    if (!arrayOfResults.length && templateSchema.length) {
      const newSchema = { schema: templateSchema, id: Date.now().toString() };
      arrayOfResults = [newSchema];
    }

    const newTableRows: TableComponent["rows"] = [];
    if (value && Array.isArray(value) && value.length) {
      for (const v of value) {
        const newRow: any = {
          _id: (v as any)._objId || Date.now().toString(),
        };
        if (f.params?.schema?.length) {
          for (const s of f.params.schema) {
            if (s.type === "row" && s.params?.columns?.length) {
              for (const ss of s.params.columns) {
                newRow[ss.id] = (v as any)[ss.id];
              }
            } else {
              newRow[s.id] = (v as any)[s.id];
            }
          }
        }
        newTableRows.push(newRow);
      }
    }
    tableRows = newTableRows;
    const newTableHeaders: TableComponent["headers"] = [];
    if (f.params?.schema?.length) {
      for (const s of f.params.schema) {
        if (s.type === "row" && s.params?.columns?.length) {
          for (const ss of s.params.columns) {
            newTableHeaders.push({
              key: ss.id,
              label: ss.label || ss.id,
            });
          }
        } else {
          newTableHeaders.push({
            key: s.id,
            label: s.label || s.id,
          });
        }
      }
    }
    tableHeaders = newTableHeaders;
  }

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      schemaFingerprint = fp;
      field = nextField;
      try {
        recalc();
      } catch (err) {
        console.error(
          "error recalc for schemaentry hb-input-array-objects",
          err,
        );
      }
    }
  });

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });

  function addSchema(detail: FormEvents["submit"]) {
    const newDate = Date.now().toString();
    const newSchema = { schema: field?.params?.schema!, id: newDate };
    arrayOfResults = [...arrayOfResults, newSchema];
    const newRows = JSON.parse(JSON.stringify(tableRows));
    const newObj: any = { _id: newDate };
    for (const r of Object.keys(detail)) {
      newObj[r] = detail[r];
    }

    (detail as any)["_objId"] = newDate;
    value = [...JSON.parse(JSON.stringify(value)), detail];
    newRows.push(newObj);
    tableRows = newRows;
  }
  function onTableAction(detail: TableEvents["tableaction"]) {
    arrayOfResults = JSON.parse(
      JSON.stringify(arrayOfResults.filter((f) => f.id !== detail.itemId)),
    );

    tableRows = JSON.parse(
      JSON.stringify(tableRows.filter((f) => f._id !== detail.itemId)),
    );
    value = JSON.parse(
      JSON.stringify(value.filter((f) => f._objId !== detail.itemId)),
    );
  }

  addComponent({
    repoName: "@htmlbricks/hb-form",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-table",
    version: pkg.version,
  });

  function syncNestedFormOutlined() {
    nested_form_buttons_outlined = computeColorSchemeDark() ? "yes" : "no";
  }

  onMount(() => {
    syncNestedFormOutlined();
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", syncNestedFormOutlined);

    const docRoots = [document.documentElement, document.body].filter(
      Boolean,
    ) as Element[];
    const docMo = new MutationObserver(syncNestedFormOutlined);
    for (const el of docRoots) {
      docMo.observe(el, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }

    const host = $host();
    let hostMo: MutationObserver | null = null;
    if (host) {
      hostMo = new MutationObserver(syncNestedFormOutlined);
      hostMo.observe(host, {
        attributes: true,
        attributeFilter: ["data-theme", "class"],
      });
    }

    return () => {
      mq.removeEventListener("change", syncNestedFormOutlined);
      docMo.disconnect();
      hostMo?.disconnect();
    };
  });
</script>

{#if field != null}
  <div class="field hb-input-array-objects-root">
    <div part="properties-container" class="box hb-input-array-objects-panel">
      <hb-table
        disablepagination="yes"
        actions={JSON.stringify(field?.disabled ? [] : tableActions)}
        headers={JSON.stringify(tableHeaders)}
        rows={JSON.stringify(tableRows)}
        ontableaction={(e: { detail: TableEvents["tableaction"] }) => {
          if (!field?.disabled) onTableAction(e.detail);
        }}
      ></hb-table>

      {#if !field?.disabled}
        <div part="add-section" class="hb-input-array-objects-add">
          <hb-form
            schema={JSON.stringify(
              Array.isArray(field?.params?.schema) ? field.params.schema : [],
            )}
            onsubmit={(e: { detail: FormEvents["submit"] }) => {
              addSchema(e.detail);
            }}
          >
            <span slot="submit_button">
              <button
                type="button"
                part="add-button"
                class="button is-primary hb-input-array-objects-add-button"
                class:is-outlined={nested_form_buttons_outlined === "yes"}
              >
                <slot name="add-object-label">
                  {field?.params?.addPropertyLabel
                    ? field.params.addPropertyLabel
                    : "Add item"}
                </slot>
              </button>
            </span>
          </hb-form>
        </div>
      {/if}
    </div>

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
